import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSVerifyPhoneOtpSubmitInput,
  type EnrollmentHYCSVerifyPhoneOtpSubmitOutput,
  type EnrollmentHYCSVerifyPhoneData,
  type EnrollmentHYCSVerifyPhoneLinkSubmitInput,
  type EnrollmentHYCSVerifyPhoneLinkSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyPhoneData>
 * @example const response = await getEnrollmentHYCSVerifyPhoneData()
 */
export const getEnrollmentHYCSVerifyPhoneData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSVerifyPhoneData',
      query: `mutation getEnrollmentHYCSVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              phoneNumber
              progress
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyPhoneData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSVerifyPhoneData = createQueryHook<undefined, EnrollmentHYCSVerifyPhoneData>(
  getEnrollmentHYCSVerifyPhoneData,
)

/**
 * Submit Enrollment HYCS VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyPhoneData();
 */
export const submitEnrollmentHYCSVerifyPhoneLinkData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentHYCSVerifyPhoneLinkSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyPhoneLinkSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSVerifyPhoneData',
      query: `mutation submitEnrollmentHYCSVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyPhoneLinkSubmitOutput }
  return response.submitEnrollment
}

/**
 * Submit Enrollment HYCS VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyPhoneData();
 */
export const submitEnrollmentHYCSVerifyPhoneOtpData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentHYCSVerifyPhoneOtpSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyPhoneOtpSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSVerifyPhoneData',
      query: `mutation submitEnrollmentHYCSVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyPhoneOtpSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSVerifyPhoneLinkData = createMutationHook<
  EnrollmentHYCSVerifyPhoneLinkSubmitInput,
  EnrollmentHYCSVerifyPhoneLinkSubmitOutput
>(submitEnrollmentHYCSVerifyPhoneLinkData)

export const useSubmitEnrollmentHYCSVerifyPhoneOTPData = createMutationHook<
  EnrollmentHYCSVerifyPhoneOtpSubmitInput,
  EnrollmentHYCSVerifyPhoneOtpSubmitOutput
>(submitEnrollmentHYCSVerifyPhoneOtpData)
